import { CreationContext, MarketCard, EventGroup } from "../../types";
import { enabledInsightSizes } from "@fresh8/types/src/featureFlags";
import { Market, SportEvent } from "@fresh8/types/src/betData";

import { Brand } from "@fresh8/types/src";

/**
 * Checks if any of the markets have any insights
 * if there are market insights MarketInsight and MarketInsightScore will no longer be optional
 * in current scope
 */

function hasInsight(market: Market): market is MarketWithInsight {
  const insightLen = market?.marketInsight?.length ?? 0;
  return insightLen > 0;
}

type MarketWithInsight = Market & {
  marketInsightScore: number;
  marketInsight: string;
};

/**
 * reduce down to market which contains the highest scoring insight
 */

function highestScoreInsightReducer(
  highest: MarketWithInsight,
  cur: MarketWithInsight
): MarketWithInsight {
  return cur.marketInsightScore > highest.marketInsightScore ? cur : highest;
}

function marketWithInsightsReducer(acc: MarketWithInsight[], curr: Market) {
  if (hasInsight(curr)) {
    acc.push(curr);
  }
  return acc;
}
/**
 * Find the market with the highest scoring insight,
 * if market has no insight return undefined (doesnt render insight card)
 */

function findMarketInsight(markets: Market[]): string[] | undefined {
  // only want to do comparisons with markets with insight data.
  const marketsWithInsight = markets.reduce(marketWithInsightsReducer, []);
  if (marketsWithInsight.length) {
    // reduce markets down to the single market that has the highest marketInsightScore
    const marketInsight = marketsWithInsight.reduce(highestScoreInsightReducer);
    // threshold for 'relevent' insight is a score of 60, if heightest scoring insight is below threshold do not render insight
    if (marketInsight.marketInsightScore < 60) return undefined;

    return [marketInsight.marketId];
  }
  return undefined;
}

export function insightIsEnabled(brand: Brand, event: SportEvent): boolean {
  const availableLeagues: Record<string, string[]> = {
    "American Football": ["NFL"],
    Baseball: ["MLB", "Major League Baseball"],
    Basketball: ["NBA"],
    "Ice Hockey": ["NHL", "National Hockey League"]
  };

  let isQualifyingCompetition = false;

  if (availableLeagues[event.sport] && event.normalisedCompetition) {
    isQualifyingCompetition = availableLeagues[event.sport].includes(
      event.normalisedCompetition
    );
  }

  if (brand?.adTemplate?.insights?.enabledSports && isQualifyingCompetition) {
    return brand?.adTemplate?.insights?.enabledLeagues?.includes(
      event.normalisedCompetition as any
    );
  }

  if (brand?.adTemplate?.insights?.enabledSports?.length) {
    return brand?.adTemplate?.insights?.enabledSports?.includes(
      event.sport as any
    );
  }

  return false;
}

export function createInsightCard(
  event: EventGroup,
  context: CreationContext
): Array<MarketCard> {
  if (!enabledInsightSizes.includes(context.size.name.toLocaleLowerCase()))
    return [];

  if (
    !insightIsEnabled(context.brand, event.data) &&
    !context.insightABTestingEnabled
  )
    return [];

  const markets = event.data.markets.reduce(
    (acc: Market[], marketId) => acc.concat(context.betData.markets[marketId]),
    []
  );

  const insightMarkets = findMarketInsight(markets);

  if (!insightMarkets) return [];

  return [
    new MarketCard({
      type: "insight",
      eventId: event.id,
      marketIds: insightMarkets,
      optionIds: null
    })
  ];
}
